import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CalendarClock, CheckCircle2, FileText, Loader2, Phone, XCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { usePublicQuote, useRespondToQuote, TOKEN_RE, type PublicQuote } from '@/hooks/usePublicQuote';
import { canRespondToQuote, computeQuoteTotals, effectiveQuoteStatus, formatQuoteMoney, formatVatRate } from '@/lib/quotes';
import { cn } from '@/lib/utils';

const acceptSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Please type your full name')
    .max(120, 'That name is too long'),
  agree: z.literal(true, { errorMap: () => ({ message: 'Please tick to confirm' }) }),
});

const declineSchema = z.object({
  reason: z.string().trim().max(1000, 'Please keep it under 1000 characters'),
});

const longDate = (value: string | null | undefined) => {
  if (!value) return '';
  try {
    return format(parseISO(value), 'd MMMM yyyy');
  } catch {
    return value;
  }
};

const Shell = ({ children }: { children: React.ReactNode }) => (
  <Layout>
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
    <section className="tp-section bg-background py-10 md:py-16">
      <div className="container mx-auto max-w-3xl px-4 md:px-6">{children}</div>
    </section>
  </Layout>
);

const Message = ({ icon, title, children }: { icon: React.ReactNode; title: string; children?: React.ReactNode }) => (
  <div className="tp-card p-8 text-center">
    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-primary [&_svg]:h-6 [&_svg]:w-6">
      {icon}
    </div>
    <h1 className="tp-h3 text-foreground">{title}</h1>
    {children && <div className="mt-2 text-muted-foreground">{children}</div>}
  </div>
);

function AcceptForm({
  quote,
  token,
  selected,
}: {
  quote: PublicQuote;
  token: string;
  selected: string[];
}) {
  const respond = useRespondToQuote();
  const form = useForm<z.infer<typeof acceptSchema>>({
    resolver: zodResolver(acceptSchema),
    defaultValues: { name: '', agree: false as unknown as true },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await respond.mutateAsync({ token, action: 'accept', name: values.name, selectedLineIds: selected });
    } catch (error) {
      form.setError('root', { message: error instanceof Error ? error.message : 'Something went wrong. Please try again.' });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-4" aria-label="Accept quote">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Your full name</FormLabel>
              <FormControl>
                <Input autoComplete="name" placeholder={quote.client_name} {...field} />
              </FormControl>
              <FormDescription>Typing your name acts as your signature.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="agree"
          render={({ field }) => (
            <FormItem className="flex items-start gap-2.5 space-y-0">
              <FormControl>
                <Checkbox checked={field.value === true} onCheckedChange={(v) => field.onChange(v === true)} aria-label="I accept this quote" />
              </FormControl>
              <div>
                <FormLabel className="font-normal">
                  I accept this quote from {quote.business.name ?? 'the business'}
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        {form.formState.errors.root && (
          <p className="text-sm text-destructive" role="alert">
            {form.formState.errors.root.message}
          </p>
        )}
        <Button type="submit" size="lg" className="w-full" disabled={respond.isPending}>
          {respond.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
          Accept quote
        </Button>
      </form>
    </Form>
  );
}

function DeclineForm({ token, onCancel }: { token: string; onCancel: () => void }) {
  const respond = useRespondToQuote();
  const form = useForm<z.infer<typeof declineSchema>>({
    resolver: zodResolver(declineSchema),
    defaultValues: { reason: '' },
  });
  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await respond.mutateAsync({ token, action: 'decline', reason: values.reason });
    } catch (error) {
      form.setError('root', { message: error instanceof Error ? error.message : 'Something went wrong. Please try again.' });
    }
  });
  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-4" aria-label="Decline quote">
        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Anything you'd like to tell us? (optional)</FormLabel>
              <FormControl>
                <Textarea rows={3} placeholder="e.g. found another cleaner, price, timing…" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {form.formState.errors.root && (
          <p className="text-sm text-destructive" role="alert">
            {form.formState.errors.root.message}
          </p>
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button type="submit" variant="outline" className="flex-1" disabled={respond.isPending}>
            {respond.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Decline quote
          </Button>
          <Button type="button" variant="ghost" className="flex-1" onClick={onCancel}>
            Go back
          </Button>
        </div>
      </form>
    </Form>
  );
}

const QuotePage = () => {
  const { token } = useParams<{ token: string }>();
  const validToken = Boolean(token && TOKEN_RE.test(token));
  const { data: quote, isLoading, isError } = usePublicQuote(token);
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const [declining, setDeclining] = useState(false);

  const status = quote ? effectiveQuoteStatus(quote.status, quote.valid_until) : null;
  const open = quote ? canRespondToQuote(quote.status, quote.valid_until) : false;

  // Live totals: while open, include the extras the customer has ticked; once
  // answered, show the stored figures.
  const totals = useMemo(() => {
    if (!quote) return null;
    if (!open) {
      return { subtotal: quote.subtotal_amount, vat: quote.vat_amount, total: quote.total_amount };
    }
    return computeQuoteTotals(
      quote.lines.map((l) => ({ ...l, is_selected: l.is_optional && selectedExtras.includes(l.id) })),
      quote.vat_rate,
    );
  }, [quote, open, selectedExtras]);

  if (!validToken || (!isLoading && (isError || !quote))) {
    return (
      <Shell>
        <Message icon={<FileText />} title="Quote not found">
          This link may be mistyped, or the quote may have been withdrawn. Please get in touch with the business.
        </Message>
      </Shell>
    );
  }

  if (isLoading || !quote || !totals) {
    return (
      <Shell>
        <div className="flex min-h-[40vh] items-center justify-center" aria-busy="true">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="sr-only">Loading your quote</span>
        </div>
      </Shell>
    );
  }

  const currency = quote.currency_code;
  const required = quote.lines.filter((l) => !l.is_optional);
  const extras = quote.lines.filter((l) => l.is_optional);
  const showVat = quote.vat_rate > 0;

  return (
    <Shell>
      <Helmet>
        <title>{`Quote ${quote.quote_number}${quote.business.name ? ` · ${quote.business.name}` : ''}`}</title>
      </Helmet>

      {status === 'accepted' && (
        <div className="tp-card mb-6 flex items-start gap-3 border-primary/30 p-5" role="status" data-testid="quote-accepted">
          <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-primary" />
          <div>
            <p className="font-semibold text-foreground">Thanks{quote.accepted_name ? `, ${quote.accepted_name}` : ''} — quote accepted</p>
            <p className="text-sm text-muted-foreground">
              {quote.business.name ?? 'We'} will be in touch shortly to arrange your clean.
              {quote.accepted_at ? ` Accepted on ${longDate(quote.accepted_at)}.` : ''}
            </p>
          </div>
        </div>
      )}
      {status === 'declined' && (
        <div className="tp-card mb-6 flex items-start gap-3 p-5" role="status" data-testid="quote-declined">
          <XCircle className="mt-0.5 h-6 w-6 shrink-0 text-muted-foreground" />
          <div>
            <p className="font-semibold text-foreground">You've declined this quote</p>
            <p className="text-sm text-muted-foreground">Thanks for letting us know. If anything changes, just get in touch.</p>
          </div>
        </div>
      )}
      {status === 'expired' && (
        <div className="tp-card mb-6 flex items-start gap-3 p-5" role="status" data-testid="quote-expired">
          <CalendarClock className="mt-0.5 h-6 w-6 shrink-0 text-muted-foreground" />
          <div>
            <p className="font-semibold text-foreground">This quote has expired</p>
            <p className="text-sm text-muted-foreground">Please get in touch and we'll send you an up-to-date price.</p>
          </div>
        </div>
      )}

      <article className="tp-card overflow-hidden" aria-labelledby="quote-heading">
        <header className="flex flex-col gap-4 border-b border-border p-6 sm:flex-row sm:items-start sm:justify-between md:p-8">
          <div className="flex min-w-0 items-center gap-3">
            {quote.business.logo_url && (
              <img src={quote.business.logo_url} alt="" className="h-12 w-12 shrink-0 rounded-lg object-contain" />
            )}
            <div className="min-w-0">
              <p className="truncate font-semibold text-foreground">{quote.business.name}</p>
              {quote.business.phone && (
                <a href={`tel:${quote.business.phone}`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                  <Phone className="h-3.5 w-3.5" aria-hidden="true" />
                  {quote.business.phone}
                </a>
              )}
              {quote.business.vat_number && (
                <p className="text-xs text-muted-foreground">VAT no. {quote.business.vat_number}</p>
              )}
            </div>
          </div>
          <div className="sm:text-right">
            <p className="tp-eyebrow">Quote</p>
            <h1 id="quote-heading" className="tp-h3 mt-1 text-foreground">{quote.quote_number}</h1>
            <p className="text-sm text-muted-foreground">{longDate(quote.sent_at ?? quote.created_at)}</p>
          </div>
        </header>

        <div className="grid gap-4 border-b border-border p-6 text-sm sm:grid-cols-3 md:px-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Prepared for</p>
            <p className="mt-1 font-medium text-foreground">{quote.client_name}</p>
            {quote.client_address && <p className="text-muted-foreground">{quote.client_address}</p>}
          </div>
          {quote.frequency && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">How often</p>
              <p className="mt-1 font-medium capitalize text-foreground">{quote.frequency}</p>
            </div>
          )}
          {quote.valid_until && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Valid until</p>
              <p className="mt-1 font-medium text-foreground">{longDate(quote.valid_until)}</p>
            </div>
          )}
        </div>

        <div className="p-6 md:p-8">
          <table className="w-full text-sm" data-testid="public-quote-lines">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="pb-2 font-semibold">Description</th>
                <th className="hidden pb-2 text-right font-semibold sm:table-cell">Qty</th>
                <th className="hidden pb-2 text-right font-semibold sm:table-cell">Price</th>
                <th className="pb-2 text-right font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {required.map((line) => (
                <tr key={line.id} className="border-b border-border/60 align-top">
                  <td className="py-3 pr-3 text-foreground">
                    {line.description}
                    {line.quantity !== 1 && (
                      <span className="block text-xs text-muted-foreground sm:hidden">
                        {line.quantity} × {formatQuoteMoney(line.unit_amount, currency)}
                      </span>
                    )}
                  </td>
                  <td className="hidden py-3 text-right tabular-nums text-muted-foreground sm:table-cell">{line.quantity}</td>
                  <td className="hidden py-3 text-right tabular-nums text-muted-foreground sm:table-cell">
                    {formatQuoteMoney(line.unit_amount, currency)}
                  </td>
                  <td className="py-3 text-right font-medium tabular-nums text-foreground">{formatQuoteMoney(line.line_total, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {extras.length > 0 && (
            <div className="mt-6">
              <h2 className="text-sm font-semibold text-foreground">Optional extras</h2>
              <p className="text-xs text-muted-foreground">
                {open ? 'Tick any you would like added.' : 'Extras chosen are included in the total.'}
              </p>
              <ul className="mt-3 space-y-2">
                {extras.map((line) => {
                  const checked = open ? selectedExtras.includes(line.id) : line.is_selected;
                  return (
                    <li
                      key={line.id}
                      className={cn(
                        'flex items-center justify-between gap-3 rounded-lg border border-dashed border-border p-3 text-sm',
                        checked && 'border-solid border-primary/40 bg-secondary/40',
                      )}
                    >
                      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5">
                        <Checkbox
                          checked={checked}
                          disabled={!open}
                          aria-label={`Add ${line.description}`}
                          onCheckedChange={(v) =>
                            setSelectedExtras((prev) => (v === true ? [...prev, line.id] : prev.filter((id) => id !== line.id)))
                          }
                        />
                        <span className="text-foreground">{line.description}</span>
                      </label>
                      <span className="shrink-0 font-medium tabular-nums text-foreground">
                        + {formatQuoteMoney(line.line_total, currency)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <dl className="ml-auto mt-6 max-w-xs space-y-1.5 text-sm" data-testid="public-quote-totals">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Subtotal</dt>
              <dd className="tabular-nums text-foreground">{formatQuoteMoney(totals.subtotal, currency)}</dd>
            </div>
            {showVat && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">VAT ({formatVatRate(quote.vat_rate)})</dt>
                <dd className="tabular-nums text-foreground">{formatQuoteMoney(totals.vat, currency)}</dd>
              </div>
            )}
            <div className="flex justify-between border-t border-border pt-2 text-base font-semibold">
              <dt className="text-foreground">Total</dt>
              <dd className="tabular-nums text-foreground" data-testid="public-quote-total">{formatQuoteMoney(totals.total, currency)}</dd>
            </div>
          </dl>

          {quote.notes && (
            <div className="mt-8 rounded-lg bg-muted/50 p-4 text-sm">
              <p className="font-semibold text-foreground">Notes</p>
              <p className="mt-1 whitespace-pre-line text-muted-foreground">{quote.notes}</p>
            </div>
          )}
        </div>

        {open && token && (
          <footer className="border-t border-border bg-muted/30 p-6 md:p-8">
            {declining ? (
              <DeclineForm token={token} onCancel={() => setDeclining(false)} />
            ) : (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-foreground">Happy with this quote?</h2>
                <AcceptForm quote={quote} token={token} selected={selectedExtras} />
                <button
                  type="button"
                  className="mx-auto block text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                  onClick={() => setDeclining(true)}
                >
                  No thanks, decline this quote
                </button>
              </div>
            )}
          </footer>
        )}
      </article>
    </Shell>
  );
};

export default QuotePage;
